import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ArrowLeft,
  Eye,
  Download,
  Trash2,
  RefreshCw,
  X,
  AlertCircle,
} from 'lucide-react';
import { get, del } from '../shared/apiClient';
import { ENDPOINTS, path } from '../shared/endpoints';

export interface CalculationRecordFileInfo {
  filename: string;
  size_bytes: number;
  modified_iso: string | null;
}

export interface ListCalculationRecordsResponse {
  files: CalculationRecordFileInfo[];
  directory: string;
}

const baseUrl = process.env.REACT_APP_API_BASE_URL || '/api/v1';

export const CalculationRecordsPage: React.FC = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState<CalculationRecordFileInfo[]>([]);
  const [directory, setDirectory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewContent, setViewContent] = useState<string | null>(null);
  const [viewFilename, setViewFilename] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await get<ListCalculationRecordsResponse>(ENDPOINTS.calculationRecords.list);
      const data = res.data as ListCalculationRecordsResponse | undefined;
      setFiles(Array.isArray(data?.files) ? data.files : []);
      setDirectory(data?.directory ?? null);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Failed to load calculation records';
      setError(message);
      setFiles([]);
      setDirectory(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleView = useCallback(async (filename: string) => {
    setViewFilename(filename);
    setViewContent(null);
    setViewLoading(true);
    try {
      const res = await get<string | unknown>(
        path(ENDPOINTS.calculationRecords.get, { filename })
      );
      const text = typeof res.data === 'string' ? res.data : String(res.data ?? '');
      setViewContent(text || '(Could not load content)');
    } catch {
      setViewContent('(Could not load content)');
    } finally {
      setViewLoading(false);
    }
  }, []);

  const handleDownload = useCallback((filename: string) => {
    const url = `${baseUrl}${path(ENDPOINTS.calculationRecords.download, { filename })}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleDelete = useCallback(async (filename: string) => {
    setDeleting(true);
    setError(null);
    try {
      await del(path(ENDPOINTS.calculationRecords.delete, { filename }));
      setDeleteConfirm(null);
      await fetchList();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  }, [fetchList]);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch {
      return iso;
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-2 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700"
            aria-label="Go back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <FileText className="w-8 h-8 text-slate-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Calculation Records</h1>
              <p className="text-sm text-gray-500">
                Audit files (Markdown) for each tax calculation run
              </p>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={fetchList}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading…</div>
        ) : files.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 mb-2">
              No calculation records found. Complete a tax calculation (flow or questionnaire) to generate audit files.
            </p>
            {directory && (
              <p className="text-xs text-gray-400 font-mono mt-3 break-all">
                Backend is reading from: {directory}
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Modified
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {files.map((file) => (
                  <tr key={file.filename} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">{file.filename}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatSize(file.size_bytes)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(file.modified_iso)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => handleView(file.filename)}
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-md"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(file.filename)}
                          className="p-2 text-slate-600 hover:bg-slate-100 rounded-md"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(file.filename)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* View modal */}
      {(viewFilename !== null || viewContent !== null) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {viewFilename ?? 'View'}
              </h2>
              <button
                type="button"
                onClick={() => { setViewContent(null); setViewFilename(null); }}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              {viewLoading ? (
                <div className="text-gray-500">Loading…</div>
              ) : viewContent !== null ? (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 bg-gray-50 p-4 rounded border border-gray-200">
                  {viewContent}
                </pre>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete record?</h3>
            <p className="text-gray-600 text-sm mb-4">
              <span className="font-mono">{deleteConfirm}</span> will be permanently deleted.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalculationRecordsPage;
